/**
 * @file     ShareButton.tsx
 * @readme   ./README.md
 * @purpose  Listing share button. Uses the Web Share API on mobile (native
 *           share sheet); falls back to clipboard copy + toast on desktop.
 *           When a `pdfListing` context is provided the button opens a small
 *           menu (Share, Save as PDF) instead of one-tap sharing; the PDF
 *           item hands off to the LeadCaptureContext pdf flow (formerly the
 *           standalone SavePdfButton block).
 *           @/components/lead-capture/LeadCaptureContext
 */
'use client';

import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { useAnalytics } from '@/hooks/useAnalytics';
import { reltaTrack } from '@/hooks/useReltaTrack';
import {
  useLeadCapture,
  type ListingContext,
} from '@/components/lead-capture/LeadCaptureContext';
import { listingDetailContent as L } from '@/core/content/listingDetailSharedContent';

interface Props {
  listingKey: string;
  title: string;
  // Explicit URL to share. Defaults to the current page. Needed when the
  // button renders inside a modal/sheet (e.g. the map view) where
  // window.location is the map, not the listing.
  url?: string;
  /** When set, the button opens a menu (Share, Save as PDF) instead of
   *  one-tap sharing. Requires a LeadCaptureProvider ancestor. */
  pdfListing?: ListingContext;
}

const ShareIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

/**
 * One-tap listing share: the native share sheet where there is one, else a
 * clipboard copy with a "Link copied" toast. Render `toast` once.
 */
export function useListingShare({ listingKey, title, url: urlProp }: { listingKey: string; title: string; url?: string }) {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const [open, setOpen] = useState(false);

  async function share() {
    if (typeof window === 'undefined') return;
    const url = urlProp ?? window.location.href;
    trackEvent({
      eventName: 'mls_listing_share_clicked',
      eventType: 'click',
      category: 'engagement',
      label: listingKey,
      metadata: { listingKey, hasNativeShare: typeof navigator.share === 'function' },
    });
    reltaTrack('listing.share', { listing_key: listingKey, channel: typeof navigator.share === 'function' ? 'native' : 'clipboard' });

    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, url });
        return;
      } catch {
        // User cancelled native share, fall through to copy fallback.
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setOpen(true);
    } catch {
      // Clipboard write failed (older browsers). Best-effort: do nothing.
    }
  }

  const toast = (
    <Snackbar
      open={open}
      autoHideDuration={2500}
      onClose={() => setOpen(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setOpen(false)}
        severity="success"
        variant="filled"
        sx={{ fontWeight: 500 }}
      >
        {t('pages.propertyDetail.cta.shareCopied', {
          defaultValue: 'Link copied to clipboard',
        })}
      </Alert>
    </Snackbar>
  );

  return { share, toast };
}

export function ShareButton({ listingKey, title, url, pdfListing }: Props) {
  const { theme } = useAppTheme();
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { share, toast } = useListingShare({ listingKey, title, url });
  const handleShare = share;

  function handleClick(e: MouseEvent<HTMLButtonElement>) {
    if (pdfListing) {
      setMenuAnchor(e.currentTarget);
      return;
    }
    void handleShare();
  }

  return (
    <>
      <Button
        variant="outlined"
        size="medium"
        startIcon={<ShareIcon />}
        onClick={handleClick}
        sx={{
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: `${theme.layout.radius.md}px`,
          borderColor: theme.semanticColors.borderSubtle,
          color: theme.semanticColors.textPrimary,
        }}
      >
        {t('pages.propertyDetail.cta.share', { defaultValue: 'Share' })}
      </Button>
      {pdfListing ? (
        <ShareMenu
          anchor={menuAnchor}
          onClose={() => setMenuAnchor(null)}
          onShare={() => void handleShare()}
          pdfListing={pdfListing}
        />
      ) : null}
      {toast}
    </>
  );
}

function ShareMenu({
  anchor,
  onClose,
  onShare,
  pdfListing,
}: {
  anchor: HTMLElement | null;
  onClose: () => void;
  onShare: () => void;
  pdfListing: ListingContext;
}) {
  const { t } = useTranslation();
  const { open } = useLeadCapture();
  return (
    <Menu
      anchorEl={anchor}
      open={anchor != null}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <MenuItem
        onClick={() => {
          onClose();
          onShare();
        }}
      >
        {t('pages.propertyDetail.cta.share', { defaultValue: 'Share' })}
      </MenuItem>
      <MenuItem
        onClick={() => {
          onClose();
          open({ mode: 'pdf', listing: pdfListing });
        }}
      >
        {L.cta.savePdf.defaultValue}
      </MenuItem>
    </Menu>
  );
}
